import { basename, normalizePath } from "@/lib/compiler/path-utils"
import { ExplorerNode } from "./types"

/* ── unique id generator ── */
let _counter = Date.now()
export function uid(): string {
  return `node-${++_counter}`
}

/* ── toggle folder open/close ── */
export function toggleFolder(nodes: ExplorerNode[], id: string): ExplorerNode[] {
  return nodes.map(node => {
    if (node.id === id && node.type === "folder") {
      return { ...node, isOpen: !node.isOpen }
    }
    if (node.children) {
      return { ...node, children: toggleFolder(node.children, id) }
    }
    return node
  })
}

/* ── find a node by id ── */
export function findNode(nodes: ExplorerNode[], id: string): ExplorerNode | null {
  for (const n of nodes) {
    if (n.id === id) return n
    if (n.children) {
      const found = findNode(n.children, id)
      if (found) return found
    }
  }
  return null
}

/* ── build the path string for a node ── */
export function getNodePath(nodes: ExplorerNode[], id: string, prefix = ""): string {
  for (const n of nodes) {
    const path = prefix ? `${prefix}/${n.name}` : n.name
    if (n.id === id) return path
    if (n.children) {
      const found = getNodePath(n.children, id, path)
      if (found) return found
    }
  }
  return ""
}

/* ── add a child node inside a folder ── */
export function addNode(
  nodes: ExplorerNode[],
  parentId: string,
  newNode: ExplorerNode,
): ExplorerNode[] {
  return nodes.map(node => {
    if (node.id === parentId && node.type === "folder") {
      return {
        ...node,
        isOpen: true,
        children: [...(node.children ?? []), newNode],
      }
    }
    if (node.children) {
      return { ...node, children: addNode(node.children, parentId, newNode) }
    }
    return node
  })
}

/* ── remove a node by id ── */
export function removeNode(nodes: ExplorerNode[], id: string): ExplorerNode[] {
  return nodes
    .filter(n => n.id !== id)
    .map(n =>
      n.children ? { ...n, children: removeNode(n.children, id) } : n,
    )
}

/* ── rename a node ── */
export function renameNode(
  nodes: ExplorerNode[],
  id: string,
  newName: string,
): ExplorerNode[] {
  return nodes.map(node => {
    if (node.id === id) return { ...node, name: newName }
    if (node.children) {
      return { ...node, children: renameNode(node.children, id, newName) }
    }
    return node
  })
}

/* ── move a node (drag & drop) ── */
export function moveNode(
  nodes: ExplorerNode[],
  dragId: string,
  dropTargetId: string,
): ExplorerNode[] {
  const dragged = findNode(nodes, dragId)
  if (!dragged) return nodes
  // prevent moving into self or into own descendant
  if (dragId === dropTargetId) return nodes
  if (isDescendant(dragged, dropTargetId)) return nodes

  const without = removeNode(nodes, dragId)
  return addNode(without, dropTargetId, dragged)
}

/* ── check if targetId is a descendant of node ── */
function isDescendant(node: ExplorerNode, targetId: string): boolean {
  if (!node.children) return false
  for (const child of node.children) {
    if (child.id === targetId) return true
    if (isDescendant(child, targetId)) return true
  }
  return false
}

/* ── OS / Finder file drags ──
   `types` is a DOMStringList in Safari, so it has no `includes`.
   A Finder drag is copy-only. Setting dropEffect to "move" rejects the drop. */

export function isExternalFileDrag(dataTransfer: DataTransfer | null | undefined): boolean {
  const types = dataTransfer?.types
  if (!types) return false
  for (let i = 0; i < types.length; i += 1) {
    if (types[i] === "Files") return true
  }
  return false
}

/** Files from a drop. `files` is empty on some browsers until `items` is read. */
export function filesFromDataTransfer(dataTransfer: DataTransfer): File[] {
  if (dataTransfer.files?.length) return Array.from(dataTransfer.files)
  const items = dataTransfer.items
  if (!items?.length) return []
  const files: File[] = []
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i]
    if (item.kind !== "file") continue
    const file = item.getAsFile()
    if (file) files.push(file)
  }
  return files
}

export function droppedFilesKey(files: File[]): string {
  return files.map((file) => `${file.name}:${file.size}:${file.lastModified}`).join("|")
}

type DroppedFileActions = {
  createFileAtPath: (path: string, content: string, isAsset: boolean, onInserted?: () => void) => boolean
  updateContent: (path: string, content: string) => void
  openFile: (path: string, name: string) => void
}

/** Write Finder files into the editor. Existing paths are updated instead of duplicated. */
export async function importFinderFiles(
  osFiles: File[],
  targetFolder: string | null,
  actions: DroppedFileActions,
): Promise<void> {
  for (const file of osFiles) {
    const destPath = normalizePath(targetFolder ? `${targetFolder}/${file.name}` : file.name)
    let text: string | null = null
    try {
      text = await file.text()
    } catch {
      text = null
    }
    const open = () => actions.openFile(destPath, basename(destPath))
    if (text !== null) {
      const created = actions.createFileAtPath(destPath, text, false, open)
      if (!created) {
        actions.updateContent(destPath, text)
        open()
      }
      continue
    }
    const dataUrl = await new Promise<string>((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ""))
      reader.onerror = () => resolve("")
      reader.readAsDataURL(file)
    })
    if (!dataUrl) continue
    const created = actions.createFileAtPath(destPath, dataUrl, true, open)
    if (!created) {
      actions.updateContent(destPath, dataUrl)
      open()
    }
  }
}

/* ── find the parent folder id for a given node id ── */
export function findParentId(
  nodes: ExplorerNode[],
  id: string,
  parentId: string | null = null,
): string | null {
  for (const n of nodes) {
    if (n.id === id) return parentId
    if (n.children) {
      const found = findParentId(n.children, id, n.id)
      if (found !== null) return found
    }
  }
  return null
}